import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';
import { Icon } from '~/_shared/design-system/ui/atoms/icon/icon';
import { NotificationProvider } from '~/_shared/design-system/ui/molecules/toast/notification-provider';
import { Drawer } from '~/_shared/design-system/ui/organisms/drawer/drawer';

import './authenticated-layout.css';
import { useIsDesktop } from './use-is-desktop';

/**
 * The DOM node a child page can portal per-page chrome into (e.g. the
 * Live In Club surface portals its Club-name / date / live-count cluster
 * into this slot so it lives in the platform header rather than as a
 * second header inside the page body).
 *
 * Pages call `useHeaderSlot()` and either portal into the returned
 * element when it's an `HTMLElement`, or render nothing when it's
 * falsy. The two falsy cases — `undefined` (no `AuthenticatedLayout`
 * in scope: isolated unit tests / Storybook stories) and `null`
 * (layout mounted but the slot's callback ref hasn't fired yet: SSR
 * + first client render) — are treated the same. The page must NOT
 * render the chrome inline in the falsy case: that causes a visible
 * page shift on hydration as the chrome moves inline → portal.
 */
const HeaderSlotContext = createContext<HTMLElement | null | undefined>(
	undefined,
);

const useHeaderSlot = () => useContext(HeaderSlotContext);

interface AuthenticatedLayoutProps {
	drawer_content: ReactNode;
	/**
	 * Static fallback rendered in the header alongside the portal slot.
	 * Useful for Storybook + tests; production callers typically pass
	 * `undefined` and let the active route portal its own chrome.
	 */
	header_content?: ReactNode;
	children: ReactNode;
	// Storybook + tests can force the drawer open without a click. Production
	// callers leave this undefined; Radix Dialog manages state internally.
	default_drawer_open?: boolean;
	// Controlled-mode props — pass both when the caller wants to drive
	// the drawer's open state externally (e.g. AuthenticatedRoute
	// auto-closes the drawer on every navigation). Leave both undefined
	// for uncontrolled / Storybook / test usage; Radix manages state.
	drawer_open?: boolean;
	on_drawer_open_change?: (open: boolean) => void;
}

const AuthenticatedLayout = ({
	drawer_content,
	header_content,
	children,
	default_drawer_open,
	drawer_open,
	on_drawer_open_change,
}: AuthenticatedLayoutProps) => {
	const [header_slot, set_header_slot] = useState<HTMLElement | null>(null);
	const is_desktop = useIsDesktop();

	// Crossing to desktop dismisses the drawer: the desktop shell has no
	// drawer to be open, and without this reset a stale `drawer_open: true`
	// in the controlled owner would pop the drawer back open the moment the
	// viewport crosses back below the breakpoint. (Uncontrolled Radix state
	// — the Storybook `default_drawer_open` escape hatch — is not reset;
	// that path never resizes mid-story.)
	useEffect(() => {
		if (is_desktop) {
			on_drawer_open_change?.(false);
		}
	}, [is_desktop, on_drawer_open_change]);

	// The header holds statically-known content when the caller supplies
	// `header_content`. When it doesn't, it can still be filled at runtime by
	// a page portaling chrome into the header slot — so we only mark it
	// `--collapsible` here and let CSS decide: a `:has()` guard keeps the
	// chrome the moment the slot gains children, and the collapse itself only
	// applies at the desktop breakpoint (`lg:` in authenticated-layout.css),
	// because below it the hamburger trigger occupies the header anyway.
	const header_has_static_content = header_content != null;

	// ONE permanent skeleton for both viewport variants. A live page (e.g. an
	// in-flight broadcast) lives inside `children`, and React remounts a
	// subtree whenever any ancestor's component type changes — so the
	// breakpoint switch must never swap the tree above the page. `Drawer`
	// (Radix Dialog.Root) is context-only and renders no DOM, so it wraps
	// BOTH variants permanently; the outer div / header / main all keep the
	// same type and position. Only three leaf slots vary with the viewport:
	// the persistent <aside> (desktop), the hamburger trigger (mobile), and
	// the Drawer.Body portal (mobile) — each toggling against `null` in a
	// fixed child position, which React reconciles without touching siblings.
	// Exactly one of aside / Drawer.Body renders at a time, so
	// `drawer_content` (with its fixed element ids) is never in the DOM twice.
	return (
		<Drawer
			default_open={default_drawer_open}
			open={drawer_open}
			on_open_change={on_drawer_open_change}
		>
			<div className='authenticated-layout'>
				{is_desktop ? (
					<aside className='authenticated-layout__sidebar'>
						{drawer_content}
					</aside>
				) : null}
				<div className='authenticated-layout__body'>
					<header
						className={classlist(
							'authenticated-layout__header',
							!header_has_static_content &&
								'authenticated-layout__header--collapsible',
						)}
					>
						{is_desktop ? null : (
							<Drawer.Trigger aria-label='Open menu'>
								<Icon name='menu' />
							</Drawer.Trigger>
						)}
						<div className='authenticated-layout__header-content'>
							{header_content ?? null}
							<div
								ref={set_header_slot}
								className='authenticated-layout__header-slot'
							/>
						</div>
					</header>
					<main className='authenticated-layout__main'>
						<HeaderSlotContext.Provider value={header_slot}>
							<NotificationProvider>{children}</NotificationProvider>
						</HeaderSlotContext.Provider>
					</main>
				</div>
			</div>
			{is_desktop ? null : (
				<Drawer.Body aria-label='Main menu'>{drawer_content}</Drawer.Body>
			)}
		</Drawer>
	);
};

export type { AuthenticatedLayoutProps };
export { AuthenticatedLayout, useHeaderSlot };
